import { InPersonBadge } from '@/components/blocks/badges/InPersonBadge';
import { OnlineBadge } from '@/components/blocks/badges/OnlineBadge';
import { ExternalRedirectButton } from '@/components/blocks/buttons/ExternalRedirectButton';
import { DataCard, DataCardContent, type DataBadgeVariant } from '@/components/blocks/DataCard';
import { DateTimeLabel } from '@/components/blocks/DateTimeLabel';
import DotIcon from '@/components/blocks/DotIcon';
import { ImageFallback } from '@/components/blocks/ImageFallback';
import { SpeakerChip } from '@/components/blocks/SpeakerChip';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { DEFAULT_TIME_ZONE } from '@/constants/default';
import {
  RsvpAttendButton,
  RsvpDialog,
  formatUserRsvpStatusLines,
  isEventAtCapacity,
  isUserRegisteredForEvent,
} from '@/features/EventRsvp';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import type { Event, EventSpeaker } from '@mr-website/schemas';
import { CheckCircle2 } from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';

export interface EventCardProps {
  event: Event;
  className?: string;
  badgeText?: string;
  badgeVariant?: DataBadgeVariant;
  isPersonalizationPending: boolean;
}

function formatDuration(start: string, end: string): string {
  const minutes = (new Date(end).getTime() - new Date(start).getTime()) / 60000;
  if (minutes < 60) return `${Math.round(minutes)}min`;
  const hours = minutes / 60;
  if (hours < 24) {
    const rounded = Math.round(hours * 2) / 2;
    return rounded === 1 ? '1hr' : `${rounded}hrs`;
  }
  const days = hours / 24;
  const rounded = Math.round(days * 2) / 2;
  return rounded === 1 ? '1 day' : `${rounded} days`;
}

export function EventCard({
  event,
  className,
  badgeText,
  badgeVariant,
  isPersonalizationPending,
}: EventCardProps) {
  const { isAuthenticated } = useAuth();
  const [rsvpOpen, setRsvpOpen] = useState(false);

  const accountCenterUrl = process.env.NEXT_PUBLIC_ACCOUNT_CENTER_URL;
  const duration = formatDuration(event.date_start, event.date_end);
  const visibleSpeakers = event.speakers.slice(0, 4);
  const displayRsvp = Math.max(7, event.rsvp_count);
  const isRegistered = isUserRegisteredForEvent(event);
  const statusLines = event.user_rsvp ? formatUserRsvpStatusLines(event.user_rsvp) : [];

  const imageFallbackElement = <ImageFallback subTitle={event.type} />;
  const eventHref = `/event/${event.id}`;

  function handleRsvpClick() {
    if (!isUserRegisteredForEvent(event) && isEventAtCapacity(event)) return;
    if (!isAuthenticated) {
      const redirect = encodeURIComponent(window.location.href);
      window.location.assign(`${accountCenterUrl}/login?redirect=${redirect}`);
      return;
    }
    setRsvpOpen(true);
  }

  return (
    <article className="h-full">
      <DataCard
        imageSrc={event.featured_photo ?? undefined}
        imageAlt={event.name}
        imageFallbackSrc="/static/images/event-no-image.jpg"
        imageFallbackElement={imageFallbackElement}
        href={eventHref}
        className={cn('h-full', className)}
        badgeText={badgeText}
        badgeVariant={badgeVariant}
      >
        <DataCardContent>
          {/* Title */}
          <h3 className="line-clamp-2 text-data-card-md font-bold leading-snug text-foreground m-0">
            <Link
              href={eventHref}
              prefetch={true}
              onClick={(e) => e.stopPropagation()}
              className="text-inherit no-underline hover:underline"
            >
              {event.name}
            </Link>
          </h3>

          <div className="flex items-center gap-1 text-muted-foreground">
            <DateTimeLabel
              isoDatetime={event.date_start}
              timeZone={event.time_zone || DEFAULT_TIME_ZONE}
            />
            <DotIcon size={12} className="shrink-0" aria-hidden />
            <span className="text-data-card-xs">{duration}</span>
          </div>

          {/* Address */}
          {!event.is_online ? (
            <address className="line-clamp-2 text-data-card-xs text-muted-foreground not-italic">
              {event.address}
            </address>
          ) : null}

          {/* Online / in-person badge */}
          <div>{event.is_online ? <OnlineBadge /> : <InPersonBadge />}</div>

          {/* Description */}
          {event.description ? (
            <p className="line-clamp-2 text-data-card-xs text-muted-foreground">
              {event.description}
            </p>
          ) : null}

          <div className="flex-1" />

          <Separator className="bg-border/40" />

          {/* Speakers */}
          {visibleSpeakers.length > 0 ? (
            <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
              {visibleSpeakers.map((speaker: EventSpeaker) => (
                <SpeakerChip
                  key={speaker.name}
                  name={speaker.name}
                  headshotUrl={speaker.headshot_image_url}
                  size="sm"
                />
              ))}
            </div>
          ) : null}

          {/* RSVP count */}
          <div className="flex items-center gap-1.5">
            <span className="size-2 shrink-0 rounded-full bg-emerald-500" aria-hidden />
            <span className="text-data-card-xs text-muted-foreground">
              {displayRsvp} others going
            </span>
          </div>

          {/* CRM link, RSVP/Details, registration status — depends on
              user-specific data that only resolves client-side after
              hydration; show a skeleton rather than flash the anonymous
              state and then swap it. */}
          {isPersonalizationPending ? (
            <div className="flex gap-2">
              <Skeleton className="h-10.5 flex-[2] rounded-lg" />
              <Skeleton className="h-10.5 flex-1 rounded-lg" />
            </div>
          ) : (
            <>
              {event.crm_link ? (
                <ExternalRedirectButton
                  href={event.crm_link}
                  label="CRM"
                  className="w-full bg-employee-only-button"
                  newTab
                />
              ) : null}

              {event.is_private ? (
                <Button
                  variant="outline"
                  render={<Link href={eventHref} prefetch={true} />}
                  nativeButton={false}
                  className="w-full py-5 text-data-card-md font-bold"
                  onClick={(e) => e.stopPropagation()}
                >
                  Details
                </Button>
              ) : isRegistered ? (
                <div className="flex items-center justify-between gap-2">
                  <div className="space-y-0.5">
                    {statusLines.map((line) => (
                      <div
                        key={line}
                        className="flex items-center gap-1 text-data-card-xs text-emerald-700"
                      >
                        <CheckCircle2 className="size-3.5 shrink-0" aria-hidden />
                        <span>{line}</span>
                      </div>
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    render={<Link href={eventHref} prefetch={true} />}
                    nativeButton={false}
                    className="shrink-0 py-5 px-4 text-data-card-md font-bold"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Details
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <RsvpAttendButton
                    event={event}
                    isAuthenticated={isAuthenticated}
                    className="flex-[2] py-5 text-data-card-xs font-bold text-white"
                    style={{ backgroundColor: 'var(--color-mr-teal-dark)' }}
                    onClick={(clickEvent) => {
                      clickEvent.stopPropagation();
                      handleRsvpClick();
                    }}
                  />
                  <Button
                    variant="outline"
                    render={<Link href={eventHref} prefetch={true} />}
                    nativeButton={false}
                    className="flex-1 py-5 text-data-card-md font-bold"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Details
                  </Button>
                </div>
              )}
            </>
          )}
        </DataCardContent>
      </DataCard>
      {!event.is_private ? (
        <RsvpDialog event={event} open={rsvpOpen} onOpenChange={setRsvpOpen} />
      ) : null}
    </article>
  );
}
